import { useState } from 'react';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  useMesTests,
  usePassationEspace,
  useRemettreEspace,
  type MonTest,
} from '../../lib/useTestsPositionnement';
import { PassationTest } from './PassationTest';

function jour(valeur: string): string {
  return new Date(valeur).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

const TONS: Record<MonTest['etat'], string> = {
  A_PASSER: 'warning',
  A_CORRIGER: 'info',
  CORRIGE: 'success',
};

function Passation({ testId, onFermer }: { testId: string; onFermer: () => void }): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = usePassationEspace(testId);
  const remettre = useRemettreEspace();

  if (isLoading) return <Chargement libelle="Chargement du test…" />;
  if (error instanceof ErreurApi) return <MessageErreur message={error.message} />;
  if (!data) return <MessageErreur message="Le test n’a pas pu être chargé." />;

  return (
    <PassationTest
      passation={data}
      enCours={remettre.isPending}
      erreur={remettre.error instanceof ErreurApi ? remettre.error.message : null}
      onAnnuler={onFermer}
      onRemettre={(reponses) =>
        remettre.mutate(
          { testId, reponses },
          {
            onSuccess: ({ message }) => {
              notifier(message);
              onFermer();
            },
          },
        )
      }
    />
  );
}

/**
 * Tests de positionnement attribués à l'apprenant (CDC §6.5) : il les passe
 * ici, ou sur la tablette du centre. Une remise est définitive ; le score
 * n'apparaît qu'une fois la copie corrigée.
 */
export function MesTestsPositionnement(): JSX.Element | null {
  const { data, isLoading, error } = useMesTests();
  const [enCours, setEnCours] = useState<MonTest | null>(null);

  if (isLoading) return <Chargement libelle="Chargement des tests…" />;
  if (error instanceof ErreurApi) return <MessageErreur message={error.message} />;
  if (!data || data.length === 0) return null;

  return (
    <Carte titre={enCours ? enCours.titre : 'Mes tests de positionnement'} sansBordure>
      <div className="p-4 sm:p-6">
        {enCours ? (
          <Passation testId={enCours.id} onFermer={() => setEnCours(null)} />
        ) : (
          <ul className="divide-y divide-gray-100">
            {data.map((test) => (
              <li key={test.id} className="flex flex-wrap items-center gap-3 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-codialis-navy">{test.titre}</div>
                  <div className="text-[11px] text-codialis-gray">
                    {test.soumisLe ? `Remis le ${jour(test.soumisLe)}` : `Attribué le ${jour(test.attribueLe)}`}
                    {test.score !== null ? ` · ${test.score}/100` : ''}
                  </div>
                </div>
                <Pastille texte={test.etatLibelle} ton={TONS[test.etat]} />
                {test.etat === 'A_PASSER' && (
                  <button
                    type="button"
                    onClick={() => setEnCours(test)}
                    className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep"
                  >
                    Commencer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Carte>
  );
}
